/** Popup verification: method types, date format & date order (Shopify-style). */

export const POPUP_VERIFICATION_METHOD_CHOICES = [
  { label: "No input", value: "no_input" },
  { label: "Birthdate entry", value: "birthdate" },
  { label: "Checkbox", value: "checkbox" },
];

/** How dates are displayed / separated on the storefront */
export const DATE_FORMAT_TYPE_OPTIONS = [
  { label: "DD / MM / YYYY", value: "DD_MM_YYYY_SLASH" },
  { label: "MM / DD / YYYY", value: "MM_DD_YYYY_SLASH" },
  { label: "YYYY / MM / DD", value: "YYYY_MM_DD_SLASH" },
  { label: "DD - MM - YYYY", value: "DD_MM_YYYY_DASH" },
  { label: "MM - DD - YYYY", value: "MM_DD_YYYY_DASH" },
  { label: "YYYY - MM - DD", value: "YYYY_MM_DD_DASH" },
];

/** Field order (legacy / API); UI uses {@link dateOrderCommaFromDateFormat} only. */
export const DATE_ORDER_COMMA_OPTIONS = [
  { label: "YY,MM,DD", value: "YY,MM,DD" },
  { label: "MM,DD,YY", value: "MM,DD,YY" },
  { label: "DD,MM,YY", value: "DD,MM,YY" },
  { label: "YY,DD,MM", value: "YY,DD,MM" },
  { label: "MM,YY,DD", value: "MM,YY,DD" },
  { label: "DD,YY,MM", value: "DD,YY,MM" },
];

const LEGAL_ORDERS = new Set(DATE_ORDER_COMMA_OPTIONS.map((o) => o.value));

export function normalizePopupVerificationMethod(raw) {
  const s = String(raw ?? "no_input").trim().toLowerCase();
  if (s === "yesno") return "no_input";
  if (s === "dob") return "birthdate";
  if (s === "no_input" || s === "birthdate" || s === "checkbox") return s;
  return "no_input";
}

/** Map legacy DMY / stored value to current date format select value */
export function coerceDateFormatType(stored) {
  const s = String(stored || "").toUpperCase();
  if (DATE_FORMAT_TYPE_OPTIONS.some((o) => o.value === stored)) return stored;
  if (s === "DMY") return "DD_MM_YYYY_SLASH";
  if (s === "MDY") return "MM_DD_YYYY_SLASH";
  if (s === "YMD") return "YYYY_MM_DD_SLASH";
  return "DD_MM_YYYY_SLASH";
}

/** Map legacy dateOrder strings to comma order */
export function coerceDateOrderComma(stored) {
  if (stored && LEGAL_ORDERS.has(String(stored))) return String(stored);
  const o = String(stored || "").toLowerCase();
  if (o === "d-m-y" || o === "d/m/y") return "DD,MM,YY";
  if (o === "m/d/y" || o === "m-d-y") return "MM,DD,YY";
  if (o === "y-m-d" || o === "y/m/d") return "YY,MM,DD";
  return "DD,MM,YY";
}

/** `DD_MM_*` → day first, `MM_DD_*` → month first, `YYYY_MM_*` → year first (YY token for year field). */
export function parseDateOrderTokensFromDateFormat(dateFormatRaw) {
  const f = coerceDateFormatType(dateFormatRaw);
  if (f.startsWith("MM_")) return ["MM", "DD", "YY"];
  if (f.startsWith("YYYY_")) return ["YY", "MM", "DD"];
  return ["DD", "MM", "YY"];
}

/** Comma string stored on `dob.dateOrder` for older clients; derived from `dateFormat` when saving. */
export function dateOrderCommaFromDateFormat(dateFormatRaw) {
  return parseDateOrderTokensFromDateFormat(dateFormatRaw).join(",");
}

/** Separator between D/M/Y in previews (`/` vs `-`). */
export function dateFormatSeparator(dateFormatRaw) {
  const f = coerceDateFormatType(dateFormatRaw);
  return f.endsWith("_DASH") ? " - " : " / ";
}

/**
 * Token order for three DOB boxes. Prefer `dateFormat` when set; else legacy `commaOrder`.
 * @param {string} [commaOrder]
 * @param {string} [dateFormatRaw]
 */
export function parseDateOrderTokens(commaOrder, dateFormatRaw) {
  if (dateFormatRaw != null && String(dateFormatRaw).trim() !== "") {
    return parseDateOrderTokensFromDateFormat(dateFormatRaw);
  }
  const o = coerceDateOrderComma(commaOrder);
  return o.split(",").map((t) => t.trim().toUpperCase());
}

/** Checkout: date of birth or checkbox-only. Legacy `yesno` maps to `dob`. */
export function normalizeCheckoutVerificationMethod(raw) {
  const s = String(raw ?? "dob")
    .trim()
    .toLowerCase();
  if (s === "checkbox") return "checkbox";
  if (s === "yesno" || s === "yes_no" || s === "no_input") return "dob";
  if (s === "dob" || s === "birthdate") return "dob";
  return "dob";
}

export function clampPopupVerifyAge(minAgeRaw) {
  const v = Number(minAgeRaw);
  return Number.isFinite(v) && v > 0 ? Math.floor(v) : 18;
}

/** Single template minimum age — reconciles root `ageRule`, popup `verifyAge`, and checkout `ageRule.minAge`. */
export function resolveTemplateMinAge(config) {
  const c = config && typeof config === "object" ? config : {};
  const raw =
    c.ageRule?.minAge ??
    c.checkout?.ageRule?.minAge ??
    c.popup?.verifyAge ??
    18;
  return clampPopupVerifyAge(raw);
}

/** Keep root, popup, and checkout min-age fields in sync (storefront + checkout read their slice). */
export function applyTemplateMinAge(config, minAgeRaw) {
  const c = config && typeof config === "object" ? config : {};
  const minAge = clampPopupVerifyAge(minAgeRaw);
  return {
    ...c,
    ageRule: { minAge },
    popup: { ...(c.popup || {}), verifyAge: minAge },
    checkout: {
      ...(c.checkout || {}),
      ageRule: { ...(c.checkout?.ageRule || {}), minAge },
    },
  };
}

/** `{{minimum_age}}` → numeric min age (template `ageRule.minAge`, synced to popup + checkout). */
export function replaceMinimumAgePlaceholders(str, minAgeRaw) {
  const m = clampPopupVerifyAge(minAgeRaw);
  let s = String(str ?? "");
  if (!s) return s;
  s = s.replace(/\{\{\s*minimum_age\s*\}\}/gi, String(m));
  return s;
}

/** Replace `{{minimum_age}}` with the configured minimum age. */
export function formatPopupTextWithAge(str, minAgeRaw) {
  const age = clampPopupVerifyAge(minAgeRaw);
  return replaceMinimumAgePlaceholders(String(str ?? ""), age);
}

export function popupAcceptLabel(text, minAgeRaw) {
  const age = clampPopupVerifyAge(minAgeRaw);
  const t = String(text ?? "").trim();
  if (!t) return "";
  return formatPopupTextWithAge(t, age);
}

export function popupRejectLabel(text, minAgeRaw) {
  const age = clampPopupVerifyAge(minAgeRaw);
  const t = String(text ?? "").trim();
  if (!t) return "";
  return formatPopupTextWithAge(t, age);
}

/** Default copy when “Checkbox title” is left empty (all popup layouts). */
export const DEFAULT_POPUP_CHECKBOX_LABEL = "I confirm I am {{minimum_age}}+";

export function popupCheckboxConfirmLabel(text, minAgeRaw) {
  const age = clampPopupVerifyAge(minAgeRaw);
  const t = String(text ?? "").trim();
  const base = t || DEFAULT_POPUP_CHECKBOX_LABEL;
  return formatPopupTextWithAge(base, age);
}
